// BannerBlock.jsx
import { useAccessibility } from '../../../context/AccessibilityContext/AccessibilityContext';
import Block from '../../../components/Block/Block';
import style from './bannerblock.module.scss';

const BannerBlock = ({ facility }) => {
    const { settings } = useAccessibility();

    // Accessibility-aware styles
    const bannerStyles = {
        backgroundColor: settings.contrast === 'default' ? '#0a0a2e' : 'var(--bg-primary)',
        color: settings.contrast === 'default' ? '#fff' : 'var(--text-primary)',
        textShadow: settings.contrast === 'default' ? '0 2px 10px rgba(0,0,0,0.3)' : 'none',
    };

    // Function to get image URL from Strapi
    const getImageUrl = () => {
        if (!facility) return null;
        
        const imageData = facility.attributes?.Image;
        
        if (!imageData) return null;

        // Helper to process image URL
        const processUrl = (url) => {
            if (!url) return null;
            return url.startsWith('http') ? url : `${process.env.NEXT_PUBLIC_STRAPI_URL || 'http://localhost:1337'}${url}`;
        };

        // Handle different image data structures
        if (imageData.data) {
            if (Array.isArray(imageData.data) && imageData.data.length > 0) {
                return processUrl(imageData.data[0].attributes?.url);
            }
            if (imageData.data.attributes?.url) {
                return processUrl(imageData.data.attributes.url);
            }
        }
        
        if (Array.isArray(imageData) && imageData.length > 0) {
            const firstImage = imageData[0];
            return processUrl(firstImage.url || firstImage.attributes?.url);
        }
        
        if (typeof imageData === 'string') return processUrl(imageData);
        if (imageData.url) return processUrl(imageData.url);

        return null;
    };

    const imageUrl = getImageUrl();
    const facilityTitle = facility?.attributes?.Title || 'Филиал';
    const facilityAddress = facility?.attributes?.Address || 'Адрес не указан';

    return (
        <Block
            flexDirection='column'
            justifyContent='center'
            alignItems='center'
            width="100%"
            height='220px' // Увеличил высоту блока для комфортного размещения фото
            background={bannerStyles.backgroundColor}
            role="banner"
            aria-label={`Баннер филиала ${facilityTitle}`}
            className={style.bannerWrapper}
        >
            <div className={style.content}>
                {/* Decorative background elements */}
                <div className={style.wavesContainer} aria-hidden="true">
                    <div className={style.backgroundOverlay} />
                    <div className={style.sportWaves} />
                </div>

                {/* Main content */}
                <div className={style.leftContent}>
                    <div className={style.textContainer}>
                        <h1 
                            className={style.title}
                            style={{ 
                                color: bannerStyles.color,
                                textShadow: bannerStyles.textShadow
                            }}
                        >
                            {facilityTitle}
                        </h1>
                        <div className={style.addressContainer}>
                            <span className={style.addressIcon} aria-hidden="true">📍</span>
                            <p 
                                className={style.address}
                                style={{ color: bannerStyles.color }}
                            >
                                {facilityAddress}
                            </p>
                        </div>
                        {facility?.attributes?.Description && (
                            <p className={style.description}>
                                {facility.attributes.Description}
                            </p>
                        )}
                    </div>
                </div>

                {/* Image section */}
                <div className={style.rightContent}>
                    {imageUrl ? (
                        <div className={style.imageWrapper}>
                            <img
                                className={style.unitImage}
                                src={imageUrl}
                                alt={`Фото филиала ${facilityTitle}`}
                                loading="lazy"
                                onError={(e) => {
                                    console.error('❌ Image failed to load:', imageUrl);
                                    e.target.style.display = 'none';
                                    e.target.parentElement.querySelector('.fallback-icon').style.display = 'flex';
                                }}
                            />
                            <div className={style.fallbackIcon} style={{ display: 'none' }}>
                                <span>🏢</span>
                            </div>
                        </div>
                    ) : (
                        <div className={style.placeholderImage}>
                            <span>🏢</span>
                            <p>Нет фото</p>
                        </div>
                    )}
                </div>
            </div>
        </Block>
    );
};

export default BannerBlock;